import { RouteRecordName, RouteRecordRaw } from 'vue-router';
import { ICommunityPageLinkDefinition, ICommunityPageLinkTreeNode } from '@/interfaces/community/ICommunityPageLink.ts';
import { ICommunityThreadPageLink } from '@/interfaces/community/ICommunityThread.ts';
import { routes } from '@/routes/routes.ts';
import { SectionRouteNames } from '@/routes/sectionRoutes/sectionRouteNames.ts';

export const COMMUNITY_ROOT_PAGE_LINK_KEY = 'community.root';

export interface ICommunityThreadPageLinkDisplay {
    key: string;
    label: string;
    routeName: string;
}

interface IRouteTreeBuildResult {
    nodes: Array<ICommunityPageLinkTreeNode>;
    definitions: Array<ICommunityPageLinkDefinition>;
}

function routeNameToString (name: RouteRecordName | null | undefined): string {
    return typeof name === 'string' ? name : '';
}

function routeLabel (route: RouteRecordRaw): string {
    return String(route.meta?.displayTitle ?? route.meta?.headName ?? route.name ?? route.path);
}

function routeIsExcluded (route: RouteRecordRaw): boolean {
    const routeName = routeNameToString(route.name);
    const searchMeta = route.meta?.searchMeta as { noSearch?: boolean; isSiteAdmin?: boolean; isEnactAdmin?: boolean } | undefined;
    const hasChildren = !!route.children?.length;

    return !routeName
        || routeName === '404'
        || routeName === 'Terms'
        || routeName === 'admin'
        || (searchMeta?.noSearch === true && !hasChildren)
        || searchMeta?.isSiteAdmin === true
        || searchMeta?.isEnactAdmin === true
        || (!!route.redirect && !hasChildren);
}

function combineRoutePath (parentPath: string, routePath: string): string {
    if (routePath.startsWith('/')) return routePath;
    if (!routePath) return parentPath || '/';
    return `${parentPath.replace(/\/$/, '')}/${routePath}`.replace(/\/+/g, '/');
}

function getSectionName (routeName: string, ancestorRouteNames: Array<string>): string | undefined {
    return Object.values(SectionRouteNames).find(sectionRouteName => sectionRouteName === routeName || ancestorRouteNames.includes(sectionRouteName));
}

function buildRouteTree (
    routesToBuild: Array<RouteRecordRaw>,
    parentPath: string = '',
    ancestorRouteNames: Array<string> = [],
    ancestorRouteLabels: Array<string> = [],
): IRouteTreeBuildResult {
    const nodes: Array<ICommunityPageLinkTreeNode> = [];
    const definitions: Array<ICommunityPageLinkDefinition> = [];

    routesToBuild.forEach(route => {
        const routeName = routeNameToString(route.name);
        const label = routeLabel(route);
        const path = combineRoutePath(parentPath, route.path);
        const shouldIncludeRoute = !routeIsExcluded(route);
        const nextAncestorRouteNames = shouldIncludeRoute ? [...ancestorRouteNames, routeName] : ancestorRouteNames;
        const nextAncestorRouteLabels = shouldIncludeRoute ? [...ancestorRouteLabels, label] : ancestorRouteLabels;
        const childResult = buildRouteTree(route.children ?? [], path, nextAncestorRouteNames, nextAncestorRouteLabels);

        if (!shouldIncludeRoute) {
            nodes.push(...childResult.nodes);
            definitions.push(...childResult.definitions);
            return;
        }

        const definition: ICommunityPageLinkDefinition = {
            key: routeName,
            label,
            labelPath: nextAncestorRouteLabels,
            routeName,
            path,
            sectionName: getSectionName(routeName, ancestorRouteNames),
            ancestorRouteNames,
            descendantRouteNames: childResult.definitions.map(child => child.routeName),
        };

        nodes.push({
            key: routeName,
            label: definition.label,
            data: definition,
            children: childResult.nodes,
        });
        definitions.push(definition, ...childResult.definitions);
    });

    return { nodes, definitions };
}

const communityRouteTree = buildRouteTree(routes);

export const COMMUNITY_PAGE_LINK_TREE = communityRouteTree.nodes;
export const COMMUNITY_PAGE_LINK_DEFINITIONS = communityRouteTree.definitions;

const COMMUNITY_PAGE_LINKS_BY_KEY = new Map(COMMUNITY_PAGE_LINK_DEFINITIONS.map(pageLink => [pageLink.key, pageLink]));
const COMMUNITY_PAGE_LINKS_BY_ROUTE_NAME = new Map(COMMUNITY_PAGE_LINK_DEFINITIONS.map(pageLink => [pageLink.routeName, pageLink]));

export function getCommunityPageLinkDefinition (key: string): ICommunityPageLinkDefinition | undefined {
    return COMMUNITY_PAGE_LINKS_BY_KEY.get(key) ?? COMMUNITY_PAGE_LINKS_BY_ROUTE_NAME.get(key);
}

export function getCommunityPageLinkDefinitionByRouteName (routeName: string): ICommunityPageLinkDefinition | undefined {
    return COMMUNITY_PAGE_LINKS_BY_ROUTE_NAME.get(routeName);
}

export function getCommunityPageLinksForSection (sectionName: string): Array<ICommunityPageLinkDefinition> {
    return COMMUNITY_PAGE_LINK_DEFINITIONS.filter(pageLink => pageLink.sectionName === sectionName);
}

export function createCommunityThreadPageLink (definition: ICommunityPageLinkDefinition): ICommunityThreadPageLink {
    return {
        routeName: definition.routeName,
    };
}

export function formatCommunityPageLinkDefinitionLabel (definition: ICommunityPageLinkDefinition): string {
    return definition.labelPath.join(' > ');
}

export function formatCommunityPageLinkLabel (pageLink: ICommunityThreadPageLink): string {
    const definition = getCommunityPageLinkDefinitionByRouteName(pageLink.routeName);
    return definition ? formatCommunityPageLinkDefinitionLabel(definition) : pageLink.routeName;
}

export function collapseCommunityPageLinkDefinitions (
    definitions: Array<ICommunityPageLinkDefinition>,
): Array<ICommunityPageLinkDefinition> {
    return definitions.filter(definition => !definitions.some(otherDefinition => (
        otherDefinition.routeName !== definition.routeName
        && otherDefinition.descendantRouteNames.includes(definition.routeName)
    )));
}

export function getDisplayCommunityThreadPageLinks (pageLinks: Array<ICommunityThreadPageLink>): Array<ICommunityThreadPageLinkDisplay> {
    const knownDefinitions = pageLinks
        .map(pageLink => getCommunityPageLinkDefinitionByRouteName(pageLink.routeName))
        .filter((definition): definition is ICommunityPageLinkDefinition => !!definition);
    const knownRouteNames = new Set(knownDefinitions.map(definition => definition.routeName));
    const collapsedKnownPageLinks = collapseCommunityPageLinkDefinitions(knownDefinitions).map(definition => ({
        key: definition.key,
        label: formatCommunityPageLinkDefinitionLabel(definition),
        routeName: definition.routeName,
    }));
    const unknownPageLinks = pageLinks
        .filter(pageLink => !knownRouteNames.has(pageLink.routeName))
        .map(pageLink => ({
            key: pageLink.routeName,
            label: pageLink.routeName,
            routeName: pageLink.routeName,
        }));

    return [...collapsedKnownPageLinks, ...unknownPageLinks];
}

export function communityThreadMatchesRoute (threadPageLinks: Array<ICommunityThreadPageLink>, routeName: string): boolean {
    return threadPageLinks.some(pageLink => {
        const definition = getCommunityPageLinkDefinitionByRouteName(pageLink.routeName);
        return pageLink.routeName === routeName || definition?.descendantRouteNames.includes(routeName);
    });
}

export function communityThreadMatchesAnyRoute (
    threadPageLinks: Array<ICommunityThreadPageLink>,
    routeNames: Array<string>,
): boolean {
    return routeNames.some(routeName => communityThreadMatchesRoute(threadPageLinks, routeName));
}

export function getCommunityRouteSubtreeRouteNames (routeName: string): Array<string> {
    const definition = getCommunityPageLinkDefinitionByRouteName(routeName);
    return definition ? [definition.routeName, ...definition.descendantRouteNames] : [];
}
